import React, { useRef, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ArrowBack from "../../assets/icons/bx-arrow-back.svg";
import ArrowBackGray from "../../assets/icons/bx-arrow-back-gray.svg";
import Triangle from "../../assets/images/triangle-gray.svg";
import Search from "../../assets/icons/bx-search-alt-blue.svg";
import Info from "../../assets/icons/bx-info-circle-grey.svg";
import Divider from "../Divider/Divider";
import Typography from "../Typography/Typography";
import useFeedbackScore from "../../store/Querys/useFeedbackScore";
import useOrganisation from "../../store/Querys/useOrganisation";

dayjs.extend(customParseFormat);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const FeedbackScoreChart = ({ subUserId }: { subUserId: string }) => {
  const { data: adminData } = useOrganisation();
  const orgId = adminData?.organizations?.[0]?.id || "";
  const { hasNextPage, fetchNextPage, data, isFetchingNextPage } =
    useFeedbackScore(orgId, subUserId);

  const [currentPage, setCurrentPage] = useState(1);
  const currentPageData = data?.pages[currentPage - 1]?.data || [];

  const gradientRef = useRef<CanvasGradient | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);

  const actualData = [...currentPageData].reverse().map((item) => ({
    value: item.avgScore,
    dateLabel: dayjs(item.date, "DD-MM-YYYY").format("DD"),
    monthLabel: dayjs(item.date, "DD-MM-YYYY").format("MMM"),
    isPlaceholder: false,
  }));

  const placeholders = Array(Math.max(0, 5 - actualData.length)).fill({
    value: 1,
    dateLabel: "",
    monthLabel: "",
    isPlaceholder: true,
  });

  const barData = [...placeholders, ...actualData];

  const chartData = {
    labels: barData.map((item) => `${item.dateLabel} ${item.monthLabel}`),
    datasets: [
      {
        label: "Practice score",
        data: barData.map((item) => item.value),
        backgroundColor: gradientRef.current || "#2E3BC7",
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  useEffect(() => {
    if (!gradientRef.current) {
      const ctx = document.createElement("canvas").getContext("2d");
      if (ctx) {
        const gradientBg = ctx.createLinearGradient(0, 150, 0, 0);
        gradientBg.addColorStop(0, "#2E3BC7");
        gradientBg.addColorStop(1, "#2e3bc799");
        gradientRef.current = gradientBg;
      }
    }
  }, []);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (!hasNextPage && data && currentPage < data.pages.length) {
      setCurrentPage((prev) => prev + 1);
    } else if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage().then(() => setCurrentPage((prev) => prev + 1));
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: (context: Context) => {
          return !barData[context.dataIndex].isPlaceholder;
        },
        anchor: "end" as "start" | "center" | "end",
        align: "end" as
          | "start"
          | "center"
          | "end"
          | "left"
          | "right"
          | "top"
          | "bottom",
        color: "#282828",
        offset: 0,
      },
    },
    scales: {
      y: {
        display: false,
        suggestedMax: 110,
      },
      x: {
        ticks: {
          color: "#868686",
          callback: function (tickValue: string | number, index: number) {
            const item = barData[index];
            return item ? [`${item.dateLabel}`, `${item.monthLabel}`] : "";
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const handleInfoClick = () => {
    setTooltipVisible((prev) => !prev);
  };

  return (
    <div
      ref={containerRef}
      className={
        "flex flex-col gap-16 rounded-20 border border-light-grey-10 bg-white p-24"
      }
    >
      <div>
        <Typography type={"Header3"} className={"left-10"}>
          Practice score
        </Typography>
        <div
          className={"flex flex-row justify-between items-center gap-8 lg:mt-8"}
        >
          <Typography
            type={"Body2"}
            typeSM={"Body3"}
            className={"!text-[#868686]"}
          >
            Your daily practice score chart.
          </Typography>
          <div
            ref={infoRef}
            onClick={handleInfoClick}
            className={
              "cursor-pointer rounded-full bg-[#F5F5FC] p-[4px] relative hover:bg-[#EAEBF9]"
            }
          >
            <img src={Info} alt={"info"} />
            {tooltipVisible && (
              <div
                className={
                  "absolute z-10 flex transform items-center lg:right-[140%] lg:top-1/2 lg:-translate-y-1/2 max-lg:left-[-52px] max-lg:top-[20px] max-lg:-translate-x-1/2 max-lg:flex-col-reverse "
                }
              >
                <div
                  className={
                    "w-200 rounded-lg border border-[#EAEBF9] bg-light-grey-8 px-16 py-12"
                  }
                >
                  <Typography type={"Body3"} className={"text-center"}>
                    The overall score is available for sessions with data from
                    active practice modes (e.g., Chat, Roleplays, and Call
                    mode).
                  </Typography>
                </div>
                <img
                  src={Triangle}
                  className="relative z-20 max-lg:left-[65px] max-lg:top-[6px] lg:rotate-180 max-lg:rotate-90"
                  alt=""
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Divider marginY={"my-0 lg:my-8 !bg-light-grey-10"} />

      {currentPageData.length > 0 && (
        <div className={"mb-2 flex items-center justify-between"}>
          <img
            src={
              data && !hasNextPage && currentPage >= data.pages.length
                ? ArrowBackGray
                : ArrowBack
            }
            alt={"back button"}
            className={`${data && !hasNextPage && currentPage >= data.pages.length ?  "" : "cursor-pointer hover:opacity-70"}`}
            onClick={handleNextPage}
          />

          <img
            src={currentPage === 1 ? ArrowBackGray : ArrowBack}
            alt={"back button"}
            className={`rotate-180 ${currentPage === 1 ? "" : "cursor-pointer hover:opacity-70"}`}
            onClick={handlePreviousPage}
          />
        </div>
      )}

      {currentPageData.length > 0 ? (
        <div className={"flex w-full max-w-3xl flex-col items-center"}>
          <div className={"w-full"}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      ) : (
        <div className={"mb-50 mt-40 flex w-full flex-col items-center"}>
          <div className={"rounded-100 bg-light-grey-10 p-16"}>
            <img src={Search} alt={"no data"} />
          </div>
          <Typography type={"Body2"} className={"pb-10 pt-20"}>
            No data
          </Typography>
          <Typography type={"Body2"} className={"!text-[#868686]"}>
            No data available.
          </Typography>
        </div>
      )}
    </div>
  );
};

export default FeedbackScoreChart;
